/**
 * GeoVera Theme System
 *
 * Provides light and dark themes with consistent design tokens
 * inspired by VS Code and modern professional GIS applications.
 */
import { createTheme, alpha } from '@mui/material/styles';

// ─── Design Tokens ───────────────────────────────────────────────

export const tokens = {
  // Spacing scale (multiples of 4px)
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  // Border radius
  radius: {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
    full: 9999,
  },
  // Font sizes
  fontSize: {
    xs: '0.65rem', // 10.4px
    sm: '0.72rem', // 11.5px
    base: '0.8rem', // 12.8px
    md: '0.85rem', // 13.6px
    lg: '0.95rem', // 15.2px
    xl: '1.1rem', // 17.6px
    xxl: '1.35rem', // 21.6px
  },
  // Layout dimensions
  layout: {
    headerHeight: 44,
    activityBarWidth: 48,
    sidePanelWidth: 280,
    sidePanelMaxWidth: 480,
    statusBarHeight: 24,
    toolbarHeight: 36,
  },
  // Animation
  animation: {
    fast: '150ms ease',
    base: '200ms ease',
    slow: '300ms ease',
  },
} as const;

// ─── Color Palettes ──────────────────────────────────────────────

const darkPalette = {
  // Background layers (darkest to lightest)
  bg: {
    base: '#1a1a1a', // App background
    surface: '#1e1e1e', // Main content
    elevated: '#252525', // Panels
    raised: '#2d2d2d', // Toolbars / app bar
    highest: '#333333', // Hover states
  },
  // Borders
  border: {
    subtle: '#2a2a2a',
    base: '#3a3a3a',
    strong: '#4a4a4a',
  },
  // Text
  text: {
    primary: '#e8e8e8',
    secondary: '#b0b0b0',
    muted: '#888888',
    disabled: '#555555',
    accent: '#4fc3f7',
  },
  // Accents
  accent: {
    primary: '#4fc3f7',
    primaryDim: 'rgba(79,195,247,0.12)',
    secondary: '#81c784',
    success: '#66bb6a',
    warning: '#ffa726',
    error: '#ef5350',
    info: '#42a5f5',
  },
};

const lightPalette = {
  bg: {
    base: '#f5f5f5',
    surface: '#ffffff',
    elevated: '#ffffff',
    raised: '#fafafa',
    highest: '#f0f0f0',
  },
  border: {
    subtle: '#eeeeee',
    base: '#e0e0e0',
    strong: '#cccccc',
  },
  text: {
    primary: '#1a1a1a',
    secondary: '#5a5a5a',
    muted: '#888888',
    disabled: '#bbbbbb',
    accent: '#0277bd',
  },
  accent: {
    primary: '#0277bd',
    primaryDim: 'rgba(2,119,189,0.08)',
    secondary: '#43a047',
    success: '#43a047',
    warning: '#f57c00',
    error: '#e53935',
    info: '#1976d2',
  },
};

// ─── Theme Builders ──────────────────────────────────────────────

export const createGeoveraTheme = (mode: 'light' | 'dark') => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: palette.accent.primary,
      },
      secondary: {
        main: palette.accent.secondary,
      },
      success: { main: palette.accent.success },
      warning: { main: palette.accent.warning },
      error: { main: palette.accent.error },
      info: { main: palette.accent.info },
      background: {
        default: palette.bg.base,
        paper: palette.bg.surface,
      },
      text: {
        primary: palette.text.primary,
        secondary: palette.text.secondary,
        disabled: palette.text.disabled,
      },
      divider: palette.border.base,
    },
    typography: {
      fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      fontSize: 13,
      h1: { fontSize: tokens.fontSize.xxl, fontWeight: 600 },
      h2: { fontSize: tokens.fontSize.xl, fontWeight: 600 },
      h3: { fontSize: tokens.fontSize.lg, fontWeight: 600 },
      h4: { fontSize: tokens.fontSize.md, fontWeight: 600 },
      h5: { fontSize: tokens.fontSize.base, fontWeight: 600 },
      h6: { fontSize: tokens.fontSize.sm, fontWeight: 600 },
      body1: { fontSize: tokens.fontSize.md },
      body2: { fontSize: tokens.fontSize.base },
      caption: { fontSize: tokens.fontSize.sm, color: palette.text.muted },
      button: { fontSize: tokens.fontSize.base, textTransform: 'none', fontWeight: 500 },
    },
    shape: {
      borderRadius: tokens.radius.md,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { height: '100%' },
          body: {
            height: '100%',
            overflow: 'hidden',
            backgroundColor: palette.bg.base,
            color: palette.text.primary,
          },
          '#root': { height: '100%' },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            background: palette.bg.base,
          },
          '*::-webkit-scrollbar-thumb': {
            background: palette.border.strong,
            borderRadius: 4,
            '&:hover': { background: palette.text.muted },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: tokens.radius.md,
            padding: '6px 14px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            transition: `background-color ${tokens.animation.fast}, color ${tokens.animation.fast}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: palette.bg.surface,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.bg.elevated,
            backgroundImage: 'none',
            borderRadius: tokens.radius.lg,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.bg.highest,
            color: palette.text.primary,
            fontSize: tokens.fontSize.sm,
            border: `1px solid ${palette.border.base}`,
            borderRadius: tokens.radius.md,
            padding: '4px 8px',
          },
          arrow: {
            color: palette.bg.highest,
            '&::before': {
              border: `1px solid ${palette.border.base}`,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            fontSize: tokens.fontSize.sm,
            height: 22,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: tokens.radius.md,
              fontSize: tokens.fontSize.md,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            fontSize: tokens.fontSize.md,
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            margin: '2px 4px',
            padding: '4px 8px',
            minHeight: 32,
            '&:hover': {
              backgroundColor: alpha(palette.accent.primary, 0.08),
            },
            '&.Mui-selected': {
              backgroundColor: palette.accent.primaryDim,
              color: palette.accent.primary,
              '&:hover': {
                backgroundColor: alpha(palette.accent.primary, 0.16),
              },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: palette.border.base,
          },
        },
      },
    },
  });
};

// Export palettes for components that need direct access
export const palettes = {
  dark: darkPalette,
  light: lightPalette,
} as const;

export type ThemeMode = 'light' | 'dark';
